import { createFileRoute, Link } from "@tanstack/react-router";
import { Crown, Gem, KeyRound, ShieldCheck, Sparkles } from "lucide-react";

import { CarGrid } from "@/components/site/CarGrid";
import { Button } from "@/components/ui/button";
import { cars, getCarFullModel } from "@/data/cars";
import { publicPageHeaders } from "@/lib/http-cache";

const CANONICAL = "https://carentals.ro/luxury-car-rental-bucharest";
const title = "Luxury Car Rental Bucharest — Premium, No Deposit";
const description =
  "Rent a luxury car in Bucharest with no deposit and no credit card. Premium BMW, Mercedes and Audi models with insurance, rovinieta and free Otopeni delivery.";

const PREMIUM_FROM = 60;

const luxuryCars = cars
  .filter((car) => car.pricePerDay >= PREMIUM_FROM)
  .sort((a, b) => b.pricePerDay - a.pricePerDay);

const cheapestLuxury = luxuryCars.length
  ? Math.min(...luxuryCars.map((car) => car.pricePerDay))
  : PREMIUM_FROM;

const faqs = [
  {
    q: "Is a deposit required for a luxury car rental in Bucharest?",
    a: "No. Every premium model is rented with zero security deposit and no credit card — a debit card or cash is enough at pickup.",
  },
  {
    q: "How much does it cost to rent a luxury car in Bucharest?",
    a: `Premium models in our fleet start at €${cheapestLuxury} per day, with full insurance, the rovinieta road tax and unlimited mileage inside Romania already included.`,
  },
  {
    q: "Can a luxury car be delivered to Otopeni Airport?",
    a: "Yes. Delivery and collection at Bucharest Henri Coandă (OTP) Airport and at our office on Bulevardul Regina Elisabeta 33 are free at any hour. Any other Bucharest address costs €20.",
  },
  {
    q: "What is the minimum age for the premium fleet?",
    a: "23 years with a driving licence held for at least 12 months for the top luxury models; 21 for the rest of the fleet.",
  },
];

export const Route = createFileRoute("/luxury-car-rental-bucharest")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      {
        name: "keywords",
        content:
          "luxury car rental bucharest, premium rent a car romania, luxury car hire bucharest, rent bmw mercedes audi bucharest, executive car rental romania",
      },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Luxury car rental in Bucharest",
          serviceType: "Luxury car rental",
          provider: { "@id": "https://carentals.ro/#organization" },
          areaServed: { "@type": "City", name: "Bucharest" },
          url: CANONICAL,
          offers: {
            "@type": "Offer",
            price: cheapestLuxury,
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
            url: CANONICAL,
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://carentals.ro/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Luxury car rental Bucharest",
              item: CANONICAL,
            },
          ],
        }),
      },
    ],
  }),
  component: LuxuryCarRentalPage,
});

const perks = [
  {
    icon: Crown,
    title: "Executive fleet",
    body: "BMW 7 Series, Mercedes GLE, Audi Q7 and other premium models kept in showroom condition.",
  },
  {
    icon: ShieldCheck,
    title: "Insurance included",
    body: "Full CASCO and RCA cover plus the rovinieta road tax are part of the daily rate, not an upsell.",
  },
  {
    icon: KeyRound,
    title: "Zero deposit",
    body: "Nothing blocked on your card and no credit card required, even on the top of the range.",
  },
  {
    icon: Sparkles,
    title: "Free delivery",
    body: "Handover at Otopeni Airport or our Regina Elisabeta 33 office at no extra cost, day or night.",
  },
];

function LuxuryCarRentalPage() {
  return (
    <div className="pb-20">
      <section className="border-b border-border bg-card/40">
        <div className="container-page py-16 sm:py-20">
          <p className="eyebrow">Premium selection</p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-5xl">
            Luxury car rental in Bucharest — no deposit, no credit card
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            {luxuryCars.length} premium cars from €{cheapestLuxury}/day, with insurance, the
            rovinieta and unlimited mileage in Romania included. Delivered free to Otopeni Airport
            or anywhere in the city centre.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/rentals">Browse the full fleet</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/car-rental-no-deposit-bucharest">How the no-deposit policy works</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-14">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {perks.map((perk) => (
            <div key={perk.title} className="rounded-2xl border border-border bg-card p-6">
              <perk.icon className="size-6 text-gold" aria-hidden="true" />
              <h2 className="mt-4 font-display text-base font-semibold">{perk.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{perk.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page pb-4">
        <h2 className="font-display text-2xl font-bold sm:text-3xl">
          Premium and luxury cars available now
        </h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Every car below rents from €{PREMIUM_FROM} per day upwards and is available with the same
          zero-deposit terms as the rest of the fleet.
        </p>
        <CarGrid cars={luxuryCars} className="mt-8" />
      </section>

      <section className="container-page py-14">
        <h2 className="font-display text-2xl font-bold sm:text-3xl">
          Luxury car rental questions
        </h2>
        <div className="mt-8 space-y-6">
          {faqs.map((faq) => (
            <div key={faq.q} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-base font-semibold">{faq.q}</h3>
              <p className="mt-2 leading-relaxed text-muted-foreground">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page">
        <div className="rounded-2xl border border-border bg-card p-8">
          <Gem className="size-6 text-gold" aria-hidden="true" />
          <h2 className="mt-4 font-display text-xl font-bold">Not sure which model to pick?</h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Popular choices for business travel are the{" "}
            {luxuryCars
              .slice(0, 3)
              .map((car) => getCarFullModel(car))
              .join(", ")}
            . Open any car page for full specs, photos and instant booking.
          </p>
          <Button asChild className="mt-6">
            <Link to="/rentals">See all cars</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
